import { RefObject, useCallback, useEffect } from 'react';
import {
  SelectionSyncMessageType,
  isPartSelectedMessage,
  isClearSelectionMessage,
} from '@physna/core/domain/viewer/selection-sync-messaging';
import { getIframeMessagingContext } from './getIframeMessagingContext';

export function useSelectionSync({
  iframeRef,
  onViewerSelect,
  onViewerClear,
}: {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onViewerSelect: (assetIds: string[], nodeNames: string[]) => void;
  onViewerClear: () => void;
}) {
  const sendSelectionToViewer = useCallback(
    (assetIds: string[], dependencyPaths?: string[]) => {
      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;

      messagingContext.iframeWindow.postMessage(
        { type: SelectionSyncMessageType.SelectParts, src: 'parent', assetIds, dependencyPaths },
        messagingContext.targetOrigin
      );
    },
    [iframeRef]
  );

  const sendClearToViewer = useCallback(() => {
    const messagingContext = getIframeMessagingContext(iframeRef);
    if (!messagingContext) return;

    messagingContext.iframeWindow.postMessage(
      { type: SelectionSyncMessageType.ClearSelection, src: 'parent' },
      messagingContext.targetOrigin
    );
  }, [iframeRef]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;
      if (event.source !== messagingContext.iframeWindow || event.origin !== messagingContext.targetOrigin) return;

      const data = event.data as unknown;

      if (isPartSelectedMessage(data)) {
        if (data.src === 'parent') return;
        onViewerSelect(data.assetIds, data.nodeNames);
        return;
      }

      if (isClearSelectionMessage(data)) {
        if (data.src === 'parent') return;
        onViewerClear();
      }
    };

    globalThis.addEventListener('message', onMessage);
    return () => globalThis.removeEventListener('message', onMessage);
  }, [iframeRef, onViewerSelect, onViewerClear]);

  return { sendSelectionToViewer, sendClearToViewer };
}
